import { HUMANOID_GEOMETRY } from '../HumanoidGeometry';
import { darken } from './svgPrims';
import type { NpcLight } from './HumanoidTypes';

/**
 * The plan-12 NPC finish: how an NPC with an art profile is lit.
 *
 * - Layered garment shading — every cloth fill becomes a vertical gradient: a
 *   cast shadow under the head, the lit band, then a hem that falls into the
 *   scene's shadow tone (canon STYLE_GUIDE §5 "body gradient", extended to
 *   clothing).
 * - A rim light along the silhouette edge facing the key light, in the home
 *   scene's light colour. Every Hollow Coil scene on the route is night or
 *   dusk, so the rim is what separates a dark coat from a dark sky.
 *
 * Players and NPCs without a profile keep the legacy flat fills (legacyPainter).
 */

export interface LightPalette {
  /** Rim colour along the lit silhouette edge. */
  rim: string;
  rimOpacity: number;
  /** Hem darkening factor (lower = deeper shadow at the feet). */
  hem: number;
  /** Shadow temperature mixed into the hem (cool for moonlight, warm for lamps). */
  shadowTint: string;
}

export const NPC_LIGHTS: Record<NpcLight, LightPalette> = {
  sun: { rim: '#fff1cf', rimOpacity: 0.55, hem: 0.8, shadowTint: '#5a3a28' },
  lamp: { rim: '#ffc06a', rimOpacity: 0.65, hem: 0.7, shadowTint: '#3a2430' },
  moon: { rim: '#bfe0ff', rimOpacity: 0.85, hem: 0.66, shadowTint: '#18243a' },
  moonfall: { rim: '#7ffff0', rimOpacity: 0.9, hem: 0.62, shadowTint: '#0c2a30' },
  overcast: { rim: '#aab3a6', rimOpacity: 0.5, hem: 0.72, shadowTint: '#2a3030' },
};

/** Turns a cloth colour into a fill. The legacy painter is the identity. */
export interface Painter {
  fill(color: string): string;
  /** `<defs>` children this painter has registered so far. */
  defs(): string;
}

export const legacyPainter: Painter = { fill: (color) => color, defs: () => '' };

/** Mix two #rrggbb colours (t = share of `b`). */
function mix(a: string, b: string, t: number): string {
  const pa = parseInt(a.replace('#', ''), 16);
  const pb = parseInt(b.replace('#', ''), 16);
  if (Number.isNaN(pa) || Number.isNaN(pb)) return a;
  const ch = (shift: number) => Math.round(((pa >> shift) & 0xff) * (1 - t) + ((pb >> shift) & 0xff) * t);
  return `#${((ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).padStart(6, '0')}`;
}

/** One gradient per distinct cloth colour, ids stable within one SVG document. */
export function finishPainter(light: NpcLight): Painter {
  const palette = NPC_LIGHTS[light];
  const ids = new Map<string, string>();
  const defs: string[] = [];
  return {
    fill(color: string): string {
      if (!/^#[0-9a-fA-F]{6}$/.test(color)) return color;
      let id = ids.get(color);
      if (!id) {
        id = `h2kCloth${ids.size}`;
        ids.set(color, id);
        const hem = mix(darken(color, palette.hem), palette.shadowTint, 0.25);
        defs.push(`<linearGradient id="${id}" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stop-color="${darken(color, 0.8)}"/>
          <stop offset="16%" stop-color="${color}"/>
          <stop offset="55%" stop-color="${darken(color, 0.93)}"/>
          <stop offset="100%" stop-color="${hem}"/>
        </linearGradient>`);
      }
      return `url(#${id})`;
    },
    defs: () => defs.join(''),
  };
}

export const NPC_RIM_FILTER_ID = 'h2kRim';

/**
 * Rim light as an SVG filter over the whole figure: the part of the silhouette
 * not covered by itself shifted away from the light becomes a thin edge,
 * tinted with the light colour and clipped back inside the silhouette.
 */
export function rimFilterDef(light: NpcLight): string {
  const p = NPC_LIGHTS[light];
  return `<filter id="${NPC_RIM_FILTER_ID}" x="-20%" y="-20%" width="140%" height="140%" color-interpolation-filters="sRGB">
    <feOffset in="SourceAlpha" dx="-4" dy="3" result="shift"/>
    <feComposite in="SourceAlpha" in2="shift" operator="out" result="edge"/>
    <feGaussianBlur in="edge" stdDeviation="0.8" result="soft"/>
    <feFlood flood-color="${p.rim}" flood-opacity="${p.rimOpacity}"/>
    <feComposite in2="soft" operator="in" result="tinted"/>
    <feComposite in="tinted" in2="SourceAlpha" operator="in" result="rim"/>
    <feMerge><feMergeNode in="SourceGraphic"/><feMergeNode in="rim"/></feMerge>
  </filter>`;
}

/** Head-and-shoulders crop for the dialog portrait, following the body-size scale. */
export interface BustFrame { x: number; y: number; width: number; height: number }

const BUST = { centerX: 62, centerY: 44, size: 104 };

export function bustFrame(scale: number): BustFrame {
  const { characterPivot } = HUMANOID_GEOMETRY;
  const s = Number.isFinite(scale) && scale > 0 ? scale : 1;
  const size = BUST.size * s;
  const cx = characterPivot.x + (BUST.centerX - characterPivot.x) * s;
  const cy = characterPivot.y + (BUST.centerY - characterPivot.y) * s;
  const round = (v: number) => Math.round(v * 100) / 100;
  return { x: round(cx - size / 2), y: round(cy - size / 2), width: round(size), height: round(size) };
}

/** Soft disc of the home light behind a bust portrait. */
export function bustBackdrop(frame: BustFrame, light: NpcLight): string {
  const p = NPC_LIGHTS[light];
  const cx = frame.x + frame.width * 0.62;
  const cy = frame.y + frame.height * 0.36;
  return `<radialGradient id="h2kBustGlow" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="${p.rim}" stop-opacity="0.32"/>
      <stop offset="100%" stop-color="${p.rim}" stop-opacity="0"/>
    </radialGradient>
    <circle data-bust-backdrop="${light}" cx="${cx}" cy="${cy}" r="${frame.width * 0.55}" fill="url(#h2kBustGlow)" />`;
}
